import {
    Button, Grid, Typography, TextField, Box, Hidden
} from "@mui/material";
import { Search as IconSearch, AddCircle as IconAddCircle } from "@mui/icons-material";
import * as ubigeoService from "../../services/ubigeoService";
import { useState } from "react";
import DataGridBase from "../../components/common/DataGridBase";
import { useMantenimiento } from "../../hooks/useMantenimiento";
import UbigeoEdit from "../../components/UbigeoEdit";

// Mantenimiento de ubigeo, con la misma forma que la pantalla de modelos:
// una lista plana con los cuatro niveles (pais, departamento, provincia y
// ubigeo) y, tras cada columna, el boton + para registrar un hijo de ese
// nivel. Se ven todas las entidades, aunque no tengan todavia hijos.
const Index = () => {
    const { isReadOnly } = useMantenimiento("ubigeo");

    const [filters, setFilters] = useState({ pais: "", departamento: "", provincia: "", ubigeo: "" });

    const [results, setResults] = useState([]);
    // El listado no se carga al abrir la pagina: se consulta al presionar
    // Buscar. Por eso el spinner empieza apagado.
    const [cargando, setCargando] = useState(false);

    const [dialog, setDialog] = useState({ open: false, nivel: 1, entity: null });
    const openDialog = (nivel, entity) => setDialog({ open: true, nivel, entity });
    const closeDialog = () => setDialog((d) => ({ ...d, open: false }));

    const setFilter = (name, value) => {
        setFilters((f) => ({ ...f, [name]: value }));
    };

    const fetchResults = async () => {
        setCargando(true);
        try {
            const data = await ubigeoService.getsMantenimientoView({ ...filters, limite: 100 });
            setResults(data || []);
        } catch (err) {
            console.log("Err: ", err);
            setResults([]);
        }
        setCargando(false);
    };

    const estiloClic = (clickeable) => ({
        cursor: clickeable ? 'pointer' : 'default',
        fontSize: 'inherit',
        textDecoration: clickeable ? 'underline' : 'none',
    });

    // Editar el pais de la fila: el id y el ISO/Num. Codigo viajan en la fila.
    const editarPais = (row) => {
        openDialog(1, { id: row.idPais, iso: row.iso || "", numCode: row.numCode || "", descripcion: row.pais || "" });
    };
    const editarDepartamento = (row) => {
        openDialog(2, { id: row.idDepartamento, idPais: row.idPais || 0, descripcion: row.departamento || "" });
    };
    const editarProvincia = (row) => {
        openDialog(3, { id: row.idProvincia, idDepartamento: row.idDepartamento || 0, descripcion: row.provincia || "" });
    };
    const editarUbigeo = (row) => {
        openDialog(4, { id: row.id, idProvincia: row.idProvincia || 0, idDepartamento: row.idDepartamento || 0, codigo: row.codigo || "", descripcion: row.ubigeo || "" });
    };

    // Registrar hijos: el padre por defecto es el de la propia fila.
    const agregarPais = () => openDialog(1, { id: 0 });
    const agregarDepartamento = (row) => openDialog(2, { id: 0, idPais: row.idPais || 0 });
    const agregarProvincia = (row) => openDialog(3, { id: 0, idDepartamento: row.idDepartamento || 0 });
    const agregarUbigeo = (row) => openDialog(4, { id: 0, idProvincia: row.idProvincia || 0, idDepartamento: row.idDepartamento || 0 });

    const celdaTexto = (valor, onClick) => (
        <Typography sx={estiloClic(!!onClick)} onClick={onClick}>
            {valor}
        </Typography>
    );

    const celdaMas = (onClick) => (isReadOnly ? null : (
        <IconAddCircle aria-label="agregar" onClick={onClick} />
    ));

    const columns = [
        { field: 'id', headerName: 'ID', width: 50, minWidth: 45, sortable: true, headerAlign: 'center', align: 'center',
            renderCell: (p) => <span style={{ fontSize: '0.75rem', color: '#666', fontFamily: 'monospace' }}>{p.value != null ? p.value : p.row.id}</span> },
        { field: 'codigo', headerName: 'Cod', width: 80, sortable: false, headerAlign: 'left', align: 'center' },
        {
            field: 'pais', headerName: 'Pais', width: 150, sortable: false, headerAlign: 'left',
            renderCell: (c) => c.row.pais == null ? null : celdaTexto(c.row.pais, () => editarPais(c.row)),
        },
        { field: 'new_pais', headerName: '', width: 30, sortable: false, headerAlign: 'center', align: 'center', renderCell: () => celdaMas(agregarPais) },
        {
            field: 'departamento', headerName: 'Departamento', width: 150, sortable: false, headerAlign: 'left',
            renderCell: (c) => c.row.departamento == null ? null : celdaTexto(c.row.departamento, () => editarDepartamento(c.row)),
        },
        { field: 'new_departamento', headerName: '', width: 30, sortable: false, headerAlign: 'center', align: 'center', renderCell: (c) => celdaMas(() => agregarDepartamento(c.row)) },
        {
            field: 'provincia', headerName: 'Provincia', width: 150, sortable: false, headerAlign: 'left',
            renderCell: (c) => c.row.provincia == null ? null : celdaTexto(c.row.provincia, () => editarProvincia(c.row)),
        },
        { field: 'new_provincia', headerName: '', width: 30, sortable: false, headerAlign: 'center', align: 'center', renderCell: (c) => celdaMas(() => agregarProvincia(c.row)) },
        {
            field: 'ubigeo', headerName: 'Ubigeo', width: 180, sortable: false, headerAlign: 'left',
            renderCell: (c) => c.row.ubigeo == null ? null : celdaTexto(c.row.ubigeo, () => editarUbigeo(c.row)),
        },
        { field: 'new_ubigeo', headerName: '', width: 30, sortable: false, headerAlign: 'center', align: 'center', renderCell: (c) => celdaMas(() => agregarUbigeo(c.row)) },
    ];

    return (
        <div>
            <Grid container>
                <Grid item>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Ubigeo
                    </Typography>
                </Grid>
                <Grid container spacing={1} padding={"12px 0px 0px 0px"}>
                    <Grid item xs={12} sm={12}>
                        <Grid container item xs={12} sm={12} spacing={1}>
                            <Grid item xs={6} sm={2}>
                                <TextField
                                    name="pais"
                                    label="Pais"
                                    value={filters.pais}
                                    fullWidth
                                    onChange={(e) => setFilter("pais", e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); fetchResults(); } }}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <TextField
                                    name="departamento"
                                    label="Departamento"
                                    value={filters.departamento}
                                    fullWidth
                                    onChange={(e) => setFilter("departamento", e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); fetchResults(); } }}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <TextField
                                    name="provincia"
                                    label="Provincia"
                                    value={filters.provincia}
                                    fullWidth
                                    onChange={(e) => setFilter("provincia", e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); fetchResults(); } }}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField
                                    name="ubigeo"
                                    label="Buscar"
                                    value={filters.ubigeo}
                                    fullWidth
                                    onChange={(e) => setFilter("ubigeo", e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); fetchResults(); } }}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={4} sm={1} display={"flex"} alignItems={"center"}>
                                <Button variant="outlined" size="medium" fullWidth onClick={fetchResults}>
                                    {<Hidden smDown></Hidden>}
                                    <IconSearch />
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                            <DataGridBase
                                rows={results}
                                columns={columns}
                                loading={cargando}
                                pageSize={100}
                                showToolbar={true}
                                getRowId={(row) => `${row.nivel}-${row.id}`}
                            />
                        </Box>
                    </Grid>
                </Grid>
                <UbigeoEdit
                    open={dialog.open}
                    openClose={closeDialog}
                    nivel={dialog.nivel}
                    entity={dialog.entity}
                    setNewSaved={fetchResults}
                    isReadOnly={isReadOnly}
                />
            </Grid>
        </div>
    );
};

export default Index;
